import { Pressable } from 'react-native';
import Animated, { FadeIn, FadeOut } from 'react-native-reanimated';

import { Stack } from '@/components/layout';
import { SvgIcon, Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import { createStyles, useScaling } from '@/hooks';
import { colors, guardianColors } from '@/theme/colors';

const strings = STRINGS.guardian.kids.eventDetail;

type EventDetailMoreMenuProps = {
  visible: boolean;
  onDismiss: () => void;
  onShare?: () => void;
};

export function EventDetailMoreMenu({ visible, onDismiss, onShare }: EventDetailMoreMenuProps) {
  const { scale } = useScaling();
  const styles = useStyles();

  if (!visible) return null;

  const handleShare = () => {
    onDismiss();
    onShare?.();
  };

  return (
    <Stack pointerEvents="box-none" style={styles.layer}>
      <Pressable
        accessibilityLabel={strings.close}
        accessibilityRole="button"
        onPress={onDismiss}
        style={styles.backdrop}
      />
      <Animated.View
        entering={FadeIn.duration(120)}
        exiting={FadeOut.duration(100)}
        style={styles.menu}>
        <Pressable
          accessibilityLabel={strings.share}
          accessibilityRole="button"
          onPress={handleShare}
          style={({ pressed }) => [styles.row, pressed ? styles.pressed : null]}>
          <Text variant="Font Guardian App/Subtitle/Subtitle 14 Semibold" style={styles.label}>
            {strings.share}
          </Text>
          <SvgIcon name="IcShare" size={scale(20)} color={guardianColors['10']} />
        </Pressable>
      </Animated.View>
    </Stack>
  );
}

const useStyles = createStyles(({ scale, deviceWidth, deviceHeight }) => ({
  layer: {
    position: 'absolute',
    top: scale(48),
    right: 0,
    zIndex: 30,
  },
  backdrop: {
    position: 'absolute',
    top: -deviceHeight,
    right: -deviceWidth,
    width: deviceWidth * 2,
    height: deviceHeight * 2,
  },
  menu: {
    width: scale(180),
    borderRadius: scale(12),
    borderCurve: 'continuous',
    overflow: 'hidden',
    backgroundColor: colors['0'],
    borderWidth: 1,
    borderColor: guardianColors['2'],
    shadowColor: guardianColors['5'],
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.3,
    shadowRadius: scale(3),
    elevation: 8,
  },
  row: {
    height: scale(48),
    paddingHorizontal: scale(12),
    flexDirection: 'row',
    alignItems: 'center',
    gap: scale(12),
  },
  label: {
    color: guardianColors['10'],
    flex: 1,
  },
  pressed: {
    opacity: 0.72,
  },
}));
